import { Button, Col, Row, Typography, Card, Divider, Tabs, Select } from "antd";
import { useSelector } from "react-redux";

import {
  selectIsUpdateProduct,
  selectProductForUpdate,
} from "../../../../redux";
import { IProducts } from "../../../../redux/@types";
import {
  InputData,
  InputHTML,
  InputNumber,
  InputSelect,
  InputText,
} from "../../../../shared/components";
import {
  productsService,
  productVariantsService,
  productComboCategoriesService,
  categoriesService,
} from "../../../../shared/services";
import { useProductModal, useProducts } from "../../hooks";
import { useFormContext } from "react-hook-form";
import { useEffect, useState } from "react";
import { ProductsGallery } from "./ProductsGallery";
import { SaveOutlined, PlusOutlined } from "@ant-design/icons";
import { TableProductWarehouses } from "../../../warehouses/ui/productByIdWarehouses";

const { Text } = Typography;

type ProductsFormProps = {
  onAssignWarehouse?: () => void;
};

export const ProductsForm = ({ onAssignWarehouse }: ProductsFormProps) => {
  const isUpdateProducts = useSelector(selectIsUpdateProduct);
  const dataUpdateProduct = useSelector(selectProductForUpdate);
  
  const [isValidate, setIsValidate] = useState(false);
  // Controlamos la pestaña activa para mover al usuario a imágenes tras crear
  const [activeTab, setActiveTab] = useState('1');
  const [arrProducts, setArrProducts] = useState<{ id: number; name: string }[]>([]);
  const [selectedVariantIds, setSelectedVariantIds] = useState<number[]>([]);
  const [comboCategories, setComboCategories] = useState<
    { category_id: number; required_quantity: number }[]
  >([]);
  const [allCategoriesFlat, setAllCategoriesFlat] = useState<
    { id: number; name: string }[]
  >([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsValidate(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const { isLoading, onSubmitCreateOrUpdate } = useProducts();
  const { arrTax } = useProductModal();

  const { control, handleSubmit: onSubmit, watch, setValue } = useFormContext<IProducts>();
  const cashback_status = watch("cashback_status");
  const multi_select_enabled = watch("multi_select_enabled");
  const is_combo = watch("is_combo");
  const promo_entry_type = watch("promo_entry_type") || "none";
  const isPromoEntry = promo_entry_type !== "none";

  // Un solo selector visual: normal / combo / entrada de promo
  const productKind =
    promo_entry_type !== "none"
      ? promo_entry_type
      : is_combo === "true"
        ? "combo"
        : "normal";

  const handleProductKindChange = (value: string) => {
    if (value === "normal") {
      setValue("is_combo", "false");
      setValue("promo_entry_type", "none");
      return;
    }
    if (value === "combo") {
      setValue("is_combo", "true");
      setValue("promo_entry_type", "none");
      return;
    }
    setValue("is_combo", "false");
    setValue("promo_entry_type", value as IProducts["promo_entry_type"]);
    setValue("price", 0);
    setValue("price_with_tax", 0);
    setValue("discount_price", 0);
    setValue("discount_price_whit_tax", 0);
    setValue("quantity", 0);
    setValue("category_id", null as any);
  };

  // Lista plana (sin árbol): el tree de productos limita raíces y no trae todas
  useEffect(() => {
    categoriesService
      .find({
        query: {
          $select: ["id", "name", "status"],
          $sort: { name: 1 },
          $limit: 999,
        },
      })
      .then((res: any) => {
        const data = Array.isArray(res) ? res : res?.data ?? [];
        setAllCategoriesFlat(
          data
            .filter((c: { id: number }) => c.id !== 1)
            .map((c: { id: number; name: string; status?: string }) => ({
              id: c.id,
              name:
                c.status && c.status !== "active"
                  ? `${c.name} (inactiva)`
                  : c.name,
            }))
        );
      })
      .catch(() => setAllCategoriesFlat([]));
  }, []);

  useEffect(() => {
    productsService
      .find({ query: { $limit: 5000 } })
      .then((res: any) => {
        const data = Array.isArray(res) ? res : res?.data ?? [];
        setArrProducts(data.map((p: any) => ({ id: p.id, name: p.name || p.code || `Producto #${p.id}` })));
      })
      .catch(() => setArrProducts([]));
  }, []);

  useEffect(() => {
    if (!isUpdateProducts || !dataUpdateProduct?.id) return;
    productVariantsService.find({ query: { product_id: dataUpdateProduct.id, $sort: { position: 1 } } })
      .then((res: any) => {
        const ids = (res.data || []).map((v: any) => v.variant_product_id);
        setSelectedVariantIds(ids);
        setValue("variant_product_ids", ids);
      })
      .catch(() => {
        setSelectedVariantIds([]);
        setValue("variant_product_ids", []);
      });
  }, [isUpdateProducts, dataUpdateProduct?.id, setValue]);

  useEffect(() => {
    setValue("variant_product_ids", selectedVariantIds);
  }, [selectedVariantIds, setValue]);

  // Cargar categorías de combo existentes al editar
  useEffect(() => {
    if (!isUpdateProducts || !dataUpdateProduct?.id) return;
    productComboCategoriesService
      .find({ query: { product_id: dataUpdateProduct.id, $sort: { position: 1 } } })
      .then((res: any) => {
        const rows = (res.data || res || []).map((r: any) => ({
          category_id: r.category_id,
          required_quantity: r.required_quantity,
        }));
        setComboCategories(rows);
      })
      .catch(() => setComboCategories([]));
  }, [isUpdateProducts, dataUpdateProduct?.id]);

  useEffect(() => {
    setValue("combo_category_configs", comboCategories);
  }, [comboCategories, setValue]);
  const price_with_tax = watch("price_with_tax");
  const tax_rule_id = watch("tax_rule_id");
  const discount_price_whit_tax = watch("discount_price_whit_tax");

  // Calcular precio sin IVA cuando el usuario ingresa precio con IVA (el backend discrimina el IVA)
  useEffect(() => {
    if (!arrTax?.length) return;
    const taxRule = arrTax.find((t) => t.id === Number(tax_rule_id));
    const taxValue = taxRule?.value ?? 0;
    if (taxValue <= 0) return;

    const priceWithTaxNum = Number(price_with_tax);
    if (!Number.isNaN(priceWithTaxNum) && priceWithTaxNum > 0) {
      const priceSinIva = priceWithTaxNum / (1 + taxValue / 100);
      setValue("price", Math.round(priceSinIva * 100) / 100);
    }
    const discountWithTaxNum = Number(discount_price_whit_tax);
    if (!Number.isNaN(discountWithTaxNum) && discountWithTaxNum > 0) {
      const discountSinIva = discountWithTaxNum / (1 + taxValue / 100);
      setValue("discount_price", Math.round(discountSinIva * 100) / 100 as number | null);
    }
  }, [price_with_tax, tax_rule_id, discount_price_whit_tax, arrTax, setValue]);

  const sectionTitleStyle = {
    fontSize: 15,
    fontWeight: 600,
    marginBottom: 16,
    color: "#1f1f1f",
    display: 'flex',
    alignItems: 'center',
    gap: 8
  };

  const comboCategoriesEditor = (
    <div style={{ marginTop: 8 }}>
      <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 6 }}>
        Categorías del combo
      </Text>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
        El cliente elige productos de cada categoría en la app. El precio cobrado es el de la pestaña &quot;Precios e Inventario&quot;, no la suma de los rollos.
      </Text>

      {comboCategories.map((cfg, index) => (
        <Row key={`combo-cat-${index}`} gutter={8} style={{ marginBottom: 8 }} align="middle">
          <Col span={14}>
            <Select
              placeholder="Categoría"
              value={cfg.category_id || undefined}
              onChange={(val) => {
                const next = [...comboCategories];
                next[index] = { ...next[index], category_id: Number(val) };
                setComboCategories(next);
              }}
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="label"
              filterOption={(input, opt) =>
                (opt?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
              }
              options={allCategoriesFlat.map((c) => ({
                value: c.id,
                label: c.name,
              }))}
            />
          </Col>
          <Col span={7}>
            <Select
              value={cfg.required_quantity || 1}
              onChange={(val) => {
                const next = [...comboCategories];
                next[index] = { ...next[index], required_quantity: Number(val) };
                setComboCategories(next);
              }}
              style={{ width: '100%' }}
              options={[1, 2, 3, 4, 5, 6].map((n) => ({ value: n, label: `${n} und.` }))}
            />
          </Col>
          <Col span={3}>
            <Button
              danger
              onClick={() =>
                setComboCategories(comboCategories.filter((_, i) => i !== index))
              }>
              X
            </Button>
          </Col>
        </Row>
      ))}

      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={() =>
          setComboCategories([
            ...comboCategories,
            { category_id: 0, required_quantity: 1 },
          ])
        }
        style={{ width: '100%' }}>
        Agregar categoría
      </Button>
    </div>
  );

  const items = [
    {
      key: '1',
      label: 'Información General',
      children: (
        <Row gutter={[32, 24]}>
          <Col span={24} lg={16}>
             <Row gutter={[24, 24]}>
                <Col span={12}>
                  <InputText
                    placeHolder="Ej: Sushi Roll Special"
                    name="name"
                    label="Nombre del producto"
                    control={control}
                  />
                </Col>
                <Col span={12}>
                  <InputText 
                    placeHolder="Ej: SKU-12345" 
                    name="code" 
                    label="Código (SKU)"
                    control={control} 
                  />
                </Col>
                <Col span={24}>
                  <div style={sectionTitleStyle}>Descripción</div>
                  <InputHTML
                    placeHolder="Describe las características principales de tu producto..."
                    name="description"
                    control={control}
                    isUpdating={isUpdateProducts}
                  />
                </Col>
             </Row>
          </Col>
          
          <Col span={24} lg={8}>
             <div style={{ background: '#fafafa', padding: 24, borderRadius: 12 }}>
                <div style={sectionTitleStyle}>Organización</div>
                <Row gutter={[24, 16]}>
                  <Col span={24}>
                    <div style={{ marginBottom: 8, fontWeight: 500 }}>
                      Tipo de producto
                    </div>
                    <Select
                      value={productKind}
                      onChange={handleProductKindChange}
                      style={{ width: "100%" }}
                      options={[
                        { label: "Producto normal", value: "normal" },
                        {
                          label: "Combo (precio fijo, el cliente arma)",
                          value: "combo",
                        },
                        {
                          label: "Entrada promo 2x1 (solo foto, abre armador)",
                          value: "2x1",
                        },
                        {
                          label: "Entrada promo 3x2 (solo foto, abre armador)",
                          value: "3x2",
                        },
                        {
                          label: "Entrada promo 50% (solo foto, abre armador)",
                          value: "fifty_off",
                        },
                      ]}
                    />
                    {isPromoEntry && (
                      <Text type="secondary" style={{ display: "block", marginTop: 8, fontSize: 12 }}>
                        Producto representativo: se lista en el home antes de las
                        categorías cuando la promo está activa (reglas en
                        Promociones / Rollos 50%). No tiene precio ni categoría;
                        al tocarlo abre el armador de la promo.
                      </Text>
                    )}
                  </Col>
                  {!isPromoEntry && (
                    <Col span={24}>
                      <InputSelect
                        name="category_id"
                        control={control}
                        showSearch
                        label="Categoría Principal"
                        dataOptions={allCategoriesFlat.map((c) => ({
                          title: c.name,
                          value: c.id,
                        }))}
                      />
                    </Col>
                  )}
                  {isUpdateProducts && (
                    <Col span={24}>
                      <InputSelect
                        name="status"
                        control={control}
                        dataOptions={[
                          { title: "Activo", value: "active" },
                          { title: "Inactivo", value: "inactive" },
                        ]}
                        label="Estado de Publicación"
                      />
                    </Col>
                  )}
                  <Col span={24}>
                    <InputNumber
                      placeHolder="0"
                      name="position_more_sales"
                      label="Posición en Ranking"
                      control={control}
                      min
                    />
                  </Col>
                </Row>
             </div>
          </Col>
          {is_combo === 'true' && (
            <Col span={24}>
              <Card
                size="small"
                title="Configuración del combo"
                style={{ borderColor: '#ff6b35' }}>
                {comboCategoriesEditor}
              </Card>
            </Col>
          )}
        </Row>
      ),
    },
    {
      key: '2',
      label: 'Imágenes y Multimedia',
      children: (
        <div style={{ minHeight: 300, padding: '10px 0' }}>
          {!isUpdateProducts && (
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              Puedes subir imágenes ahora; se asocian al producto cuando lo crees.
            </Text>
          )}
          <ProductsGallery />
        </div>
      ),
    },
    {
      key: '3',
      label: 'Precios e Inventario',
      children: isPromoEntry ? (
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <Card size="small">
              <Text>
                Este producto es solo una entrada a la promo. El precio se
                guarda en 0 y no se cobra; el valor real se calcula al armar el
                combo en la app.
              </Text>
            </Card>
          </Col>
        </Row>
      ) : (
        <Row gutter={[24, 24]}>
          {arrTax && (
            <Col span={8}>
              <InputSelect
                name="tax_rule_id"
                control={control}
                defaultValue={arrTax[0]?.id!.toString()}
                dataOptions={arrTax.map((item) => ({
                  title: `${item.name} (${item.value}%)`,
                  value: item.id.toString(),
                }))}
                label="Regla de Impuesto (IVA)"
              />
            </Col>
          )}
          <Col span={8}>
            <InputNumber
              placeHolder="0.00"
              name="price_with_tax"
              label="Precio con IVA"
              control={control}
              min
            />
          </Col>
          <Col span={8}>
            <InputNumber
              placeHolder="0.00"
              name="price"
              label="Precio sin IVA (calculado)"
              control={control}
              min
              isDisable
            />
          </Col>

          <Col span={24}>
            <Divider
              orientation="left"
              style={{ fontSize: 14, color: '#8c8c8c', margin: '12px 0 24px' }}>
              Descuentos y Ofertas
            </Divider>
          </Col>

          <Col span={8}>
            <InputNumber
              placeHolder="0.00"
              name="discount_price_whit_tax"
              label="Precio Oferta (con IVA)"
              control={control}
              min
            />
          </Col>
          <Col span={8}>
            <InputNumber
              placeHolder="0.00"
              name="discount_price"
              label="Precio Oferta sin IVA (calculado)"
              control={control}
              min
              isDisable
            />
          </Col>
          {isUpdateProducts && (
            <>
              <Col span={8}>
                <InputData
                  placeHolder="DD/MM/AAAA"
                  name="discount_start_date_price"
                  label="Inicio Oferta"
                  control={control}
                />
              </Col>
              <Col span={8}>
                <InputData
                  placeHolder="DD/MM/AAAA"
                  name="discount_end_date_price"
                  label="Fin Oferta"
                  control={control}
                />
              </Col>
            </>
          )}

          <Col span={24}>
            <Divider
              orientation="left"
              style={{ fontSize: 14, color: '#8c8c8c', margin: '12px 0 24px' }}>
              Inventario
            </Divider>
          </Col>

          <Col span={8}>
            <InputNumber
              placeHolder="0"
              name="quantity"
              label="Stock Disponible"
              control={control}
              min
            />
          </Col>
        </Row>
      ),
    },
    {
      key: '4',
      label: 'Configuración Avanzada',
      children: (
        <div style={{ maxWidth: 800, width: "100%" }}>
          <div style={sectionTitleStyle}>Características Extra</div>
          <Row gutter={[24, 24]}>
            <Col span={12}>
              <InputSelect
                name="premium"
                control={control}
                defaultValue={'false'}
                label="¿Es Producto Premium?"
                dataOptions={[
                  { title: 'Sí', value: 'true' },
                  { title: 'No', value: 'false' }
                ]}
              />
            </Col>
            <Col span={12}>
              <InputSelect
                name="sushi_almuerzo" 
                control={control}
                defaultValue={'false'}
                label="¿Aplica Sushi Almuerzo?"
                dataOptions={[
                  { title: 'Sí', value: 'true' },
                  { title: 'No', value: 'false' }
                ]}
              />
            </Col>
            <Col span={12}>
              <InputSelect
                name="multi_select_enabled"
                control={control}
                defaultValue={'false'}
                label="¿Es producto con selección múltiple?"
                dataOptions={[
                  { title: 'Sí', value: 'true' },
                  { title: 'No', value: 'false' }
                ]}
              />
            </Col>
            {multi_select_enabled === 'true' && (
              <Col span={24}>
                <div style={{ marginBottom: 8 }}>
                  <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 6 }}>
                    Productos variantes (sabores/opciones para elegir)
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                    El cliente elegirá una variante al agregar al carrito. Se cobra el precio del producto principal.
                  </Text>
                  <Select
                    mode="multiple"
                    placeholder="Selecciona los productos variantes"
                    value={selectedVariantIds}
                    onChange={(vals) => setSelectedVariantIds(vals as number[])}
                    style={{ width: '100%' }}
                    showSearch
                    optionFilterProp="label"
                    filterOption={(input, opt) =>
                      (opt?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                    }
                    options={arrProducts
                      .filter((p) => p.id !== dataUpdateProduct?.id)
                      .map((p) => ({ value: p.id, label: `${p.name} (ID: ${p.id})` }))}
                  />
                </div>
              </Col>
            )}
            <Col span={12}>
              <InputSelect
                name="cashback_status"
                control={control}
                dataOptions={[
                  { title: "Global", value: "global" },
                  { title: "Porcentaje específico", value: "specific" },
                  { title: "Inactivo", value: "inactive" },
                ]}
                label="Configuración Cashback"
              />
            </Col>
            {cashback_status === 'specific' && (
              <Col span={12}>
                <InputNumber
                  placeHolder="Ej: 10"
                  name="cashback_specific_percentage"
                  label="% Cashback (Max 50%)"
                  control={control}
                  min
                  max={50}
                />
              </Col>
            )}
          </Row>
        </div>
      ),
    },
  ];

  if (isUpdateProducts) {
    items.push({
      key: "5",
      label: "Bodega",
      children: (
        <div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
            <Button
              type="primary"
              shape="round"
              icon={<PlusOutlined />}
              size="large"
              onClick={() => onAssignWarehouse?.()}
            >
              Asignar un producto a la bodega
            </Button>
          </div>
          <TableProductWarehouses />
        </div>
      ),
    });
  }

  return (
    <>
      {isValidate && (
        <div style={{ maxWidth: 1200, margin: "0 auto", paddingBottom: 80 }}>
          <Card bordered={false} bodyStyle={{ padding: '0 24px 24px' }} style={{ width: '100%' }}>
            <Tabs 
              activeKey={activeTab} 
              onChange={setActiveTab} 
              items={items} 
              size="large" 
              animated={{ inkBar: false, tabPane: false }}
              destroyInactiveTabPane={false}
              tabBarStyle={{ marginBottom: 32 }}
              className="product-form-tabs"
            />
          </Card>

          <div style={{ 
            position: 'fixed',
            bottom: 0,
            left: 0, // Ajustar según layout real, idealmente dentro del contenedor principal si fuera relativo
            right: 0,
            background: 'white',
            padding: '16px 40px',
            borderTop: '1px solid #f0f0f0',
            display: 'flex',
            justifyContent: 'flex-end',
            zIndex: 100,
            boxShadow: '0 -2px 10px rgba(0,0,0,0.05)'
          }}>
            <div style={{ maxWidth: 1200, width: '100%', display: 'flex', justifyContent: 'flex-end', margin: '0 auto' }}>
               <Button
                type="primary"
                shape="round"
                size="large"
                onClick={onSubmit(onSubmitCreateOrUpdate)}
                loading={isLoading}
                icon={isUpdateProducts ? <SaveOutlined /> : <PlusOutlined />}
                style={{
                  height: 48,
                  padding: '0 40px',
                  fontSize: 16,
                  fontWeight: 600,
                  boxShadow: '0 4px 14px rgba(233, 87, 53, 0.3)'
                }}
              >
                {isUpdateProducts ? "Guardar Cambios" : "Crear producto"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
